import React, { createContext, useContext, useRef, useState } from "react";
import {
  DebitNote,
  DebitNoteItem,
  newDebitNote,
  newDebitNoteItem,
} from "../../../models/debit-note";
import Establishment from "../../../models/establishment";
import DispatchPoint from "../../../models/dispatch-point";
import Stamp from "../../../models/stamp";
import Client from "../../../models/client";
import { Invoice } from "../../../models/invoice";
import Errors from "../../../models/errors";
import { PYG } from "../../../currency/available-currencies";
import moment from "moment";
import { changeTZToUtc } from "../../../utils/dates";
import {
  ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE,
  ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE,
} from "../../../models/associated-document";

interface DebitNoteFormContextValue {
  // Refs and State
  debitNoteRef: React.MutableRefObject<DebitNote | null>;
  debitNoteDate: Date | undefined;
  currency: string;
  pygExchangeRate: number | undefined;
  totalAmount: number | undefined;
  amount10Percent: number | undefined;
  amount5Percent: number | undefined;
  amountExempt: number | undefined;
  iva10Percent: number | undefined;
  iva5Percent: number | undefined;
  establishment: Establishment | null;
  dispatchPoint: DispatchPoint | null;
  stamp: Stamp | null;
  client: Client | null;
  associatedDocumentType: string | undefined;
  electronicInvoice: Invoice | null;
  items: DebitNoteItem[];
  errors: Errors;
  readonly?: boolean;

  // Setters
  setDebitNoteDate: (date: Date | undefined) => void;
  setCurrency: (currency: string) => void;
  setPygExchangeRate: (rate: number | undefined) => void;
  setEstablishment: (est: Establishment | null) => void;
  setDispatchPoint: (dp: DispatchPoint | null) => void;
  setStamp: (stamp: Stamp | null) => void;
  setClient: (client: Client | null) => void;
  setAssociatedDocumentType: (type: string | undefined) => void;
  setElectronicInvoice: (invoice: Invoice | null) => void;
  setItems: (items: DebitNoteItem[]) => void;
  setErrors: (errors: Errors) => void;

  // Handlers
  onEstablishmentChange: (newEstablishment: Establishment | null) => void;
  onDispatchPointChange: (newDispatchPoint: DispatchPoint | null) => void;
  onStampChange: (newStamp: Stamp | null) => void;
  onClientChange: (newClient: Client | null) => void;
  onDebitNoteDateChange: (value: string | moment.Moment) => void;
  onCurrencyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAssociatedDocumentTypeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onElectronicInvoiceChange: (invoice: Invoice | null) => void;
  onAssociatedDocumentStampIdentifierChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentEstablishmentCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentDispatchPointCodeChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentNumberChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onAssociatedDocumentDateChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onPygExchangeRateChange: (newExchangeRate?: number) => void;
  onDncpContractCodeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDncpContractModalityChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onDncpContractDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onObsChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onItemChange: (index: number, item: DebitNoteItem) => void;
  onItemDelete: (index: number) => void;
  onItemsChange: (newItems: DebitNoteItem[]) => void;
  onAddItemClick: () => void;
}

const DebitNoteFormContext = createContext<
  DebitNoteFormContextValue | undefined
>(undefined);

export const useDebitNoteFormContext = () => {
  const context = useContext(DebitNoteFormContext);
  if (!context) {
    throw new Error(
      "useDebitNoteFormContext must be used within DebitNoteFormProvider"
    );
  }
  return context;
};

interface DebitNoteFormProviderProps {
  children: React.ReactNode;
  taxPayerId?: number;
  readonly?: boolean;
}

export const DebitNoteFormProvider: React.FC<DebitNoteFormProviderProps> = ({
  children,
  taxPayerId,
  readonly,
}) => {
  const debitNoteRef = useRef<DebitNote | null>(newDebitNote(taxPayerId));
  const [debitNoteDate, setDebitNoteDate] = useState<Date | undefined>(
    undefined
  );
  const [currency, setCurrency] = useState(PYG);
  const [pygExchangeRate, setPygExchangeRate] = useState<number | undefined>(
    undefined
  );
  const [totalAmount, setTotalAmount] = useState<number | undefined>(undefined);
  const [amount10Percent, setAmount10Percent] = useState<number | undefined>(
    undefined
  );
  const [amount5Percent, setAmount5Percent] = useState<number | undefined>(
    undefined
  );
  const [amountExempt, setAmountExempt] = useState<number | undefined>(
    undefined
  );
  const [iva10Percent, setIva10Percent] = useState<number | undefined>(
    undefined
  );
  const [iva5Percent, setIva5Percent] = useState<number | undefined>(undefined);
  const [establishment, setEstablishment] = useState<Establishment | null>(
    null
  );
  const [dispatchPoint, setDispatchPoint] = useState<DispatchPoint | null>(
    null
  );
  const [stamp, setStamp] = useState<Stamp | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [associatedDocumentType, setAssociatedDocumentType] = useState<
    string | undefined
  >(undefined);
  const [electronicInvoice, setElectronicInvoice] = useState<Invoice | null>(
    null
  );
  const [items, setItems] = useState<DebitNoteItem[]>([]);
  const [errors, setErrors] = useState<Errors>({});

  const onEstablishmentChange = (newEstablishment: Establishment | null) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.establishmentId = newEstablishment?.id;
    debitNoteRef.current.establishmentCode = newEstablishment?.code;
    setEstablishment(newEstablishment);
  };

  const onDispatchPointChange = (newDispatchPoint: DispatchPoint | null) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.dispatchPointId = newDispatchPoint?.id;
    debitNoteRef.current.dispatchPointIdentifier = newDispatchPoint?.identifier;
    setDispatchPoint(newDispatchPoint);
  };

  const onStampChange = (newStamp: Stamp | null) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.stampId = newStamp?.id;
    debitNoteRef.current.stampIdentifier = newStamp?.identifier;
    debitNoteRef.current.stampBeginDate = newStamp?.beginDate;
    setStamp(newStamp);
  };

  const onClientChange = (newClient: Client | null) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.clientId = newClient?.id;
    debitNoteRef.current.clientCode = newClient?.code;
    debitNoteRef.current.clientIsTaxPayer = newClient?.isTaxPayer;
    debitNoteRef.current.clientRuc = newClient?.ruc;
    debitNoteRef.current.clientSocialName = newClient?.socialName;
    debitNoteRef.current.clientFantasyName = newClient?.fantasyName;
    debitNoteRef.current.clientType = newClient?.type;
    debitNoteRef.current.clientCountryCode = newClient?.countryCode;
    debitNoteRef.current.clientHasAddress = newClient?.hasAddress;
    debitNoteRef.current.clientAddress = newClient?.address;
    debitNoteRef.current.clientHouseNumber = newClient?.houseNumber;
    debitNoteRef.current.clientDepartment = newClient?.department;
    debitNoteRef.current.clientDepartmentDescription =
      newClient?.departmentDescription;
    debitNoteRef.current.clientDistrict = newClient?.district;
    debitNoteRef.current.clientDistrictDescription =
      newClient?.districtDescription;
    debitNoteRef.current.clientCity = newClient?.city;
    debitNoteRef.current.clientCityDescription = newClient?.cityDescription;
    debitNoteRef.current.clientPhoneNumber = newClient?.phoneNumber;
    debitNoteRef.current.clientEmail = newClient?.email;
    setClient(newClient);
  };

  const onDebitNoteDateChange = (value: string | moment.Moment) => {
    if (debitNoteRef.current === null) return;
    const newDate = value as moment.Moment;
    if (newDate === undefined || !moment(value, true).isValid()) return;
    const debitNoteDate = new Date((value as moment.Moment).toISOString());
    setDebitNoteDate(debitNoteDate);
    debitNoteRef.current.debitNoteDate =
      changeTZToUtc(debitNoteDate).toISOString();
  };

  const onCurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.currency = e.target.value;
    setCurrency(e.target.value);
  };

  const onAssociatedDocumentTypeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (debitNoteRef.current === null) return;
    const newType = e.target.value;
    debitNoteRef.current.associatedDocumentType = newType;

    if (newType === ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE) {
      // Electronic invoice
      debitNoteRef.current.associatedDocumentPrintedType = undefined;
      debitNoteRef.current.associatedDocumentStampIdentifier = undefined;
      debitNoteRef.current.associatedDocumentEstablishmentCode = undefined;
      debitNoteRef.current.associatedDocumentDispatchPointCode = undefined;
      debitNoteRef.current.associatedDocumentNumber = undefined;
      debitNoteRef.current.associatedDocumentDate = undefined;
    } else if (newType === ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE) {
      // Printed invoice
      debitNoteRef.current.associatedDocumentCdc = undefined;
      debitNoteRef.current.invoiceId = undefined;
      debitNoteRef.current.associatedDocumentPrintedType =
        ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE;
      setElectronicInvoice(null);
    }

    setAssociatedDocumentType(newType);
  };

  const onElectronicInvoiceChange = (invoice: Invoice | null) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.invoiceId = invoice?.id;
    debitNoteRef.current.associatedDocumentCdc = invoice?.cdc;
    setElectronicInvoice(invoice);
  };

  const onAssociatedDocumentStampIdentifierChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.associatedDocumentStampIdentifier = e.target.value;
  };

  const onAssociatedDocumentEstablishmentCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.associatedDocumentEstablishmentCode = e.target.value;
  };

  const onAssociatedDocumentDispatchPointCodeChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.associatedDocumentDispatchPointCode = e.target.value;
  };

  const onAssociatedDocumentNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.associatedDocumentNumber = e.target.value;
  };

  const onAssociatedDocumentDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.associatedDocumentDate = e.target.value;
  };

  const onPygExchangeRateChange = (newExchangeRate?: number) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.pygExchangeRate = newExchangeRate;
    setPygExchangeRate(newExchangeRate);
  };

  const onDncpContractCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.dncpContractCode = e.target.value;
  };

  const onDncpContractModalityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.dncpContractModality = e.target.value;
  };

  const onDncpContractDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.dncpContractDate = e.target.value;
  };

  const onObsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (debitNoteRef.current === null) return;
    debitNoteRef.current.obs = e.target.value;
  };

  const onItemChange = (index: number, item: DebitNoteItem) => {
    if (debitNoteRef.current === null) return;
    const newItems = [...debitNoteRef.current.items];
    newItems[index] = item;
    onItemsChange(newItems);
  };

  const onItemDelete = (index: number) => {
    if (debitNoteRef.current === null) return;
    const newItems = [...debitNoteRef.current.items];
    newItems.splice(index, 1);
    onItemsChange(newItems);
  };

  const onItemsChange = (newItems: DebitNoteItem[]) => {
    if (debitNoteRef.current === null) return;

    let newTotalAmount: number | undefined = undefined;
    let newAmount10Percent: number | undefined = undefined;
    let newAmount5Percent: number | undefined = undefined;
    let newAmountExempt: number | undefined = undefined;
    let newTaxed10Percent: number | undefined = undefined;
    let newTaxed5Percent: number | undefined = undefined;
    let newIva10Percent: number | undefined = undefined;
    let newIva5Percent: number | undefined = undefined;

    for (let item of newItems) {
      if (item.unitAmount !== undefined && item.quantity !== undefined) {
        newTotalAmount = newTotalAmount
          ? newTotalAmount + item.unitAmount * item.quantity
          : item.unitAmount * item.quantity;
      }

      if (item.amount10Percent !== undefined) {
        newAmount10Percent = newAmount10Percent
          ? newAmount10Percent + item.amount10Percent
          : item.amount10Percent;
      }

      if (item.amount5Percent !== undefined) {
        newAmount5Percent = newAmount5Percent
          ? newAmount5Percent + item.amount5Percent
          : item.amount5Percent;
      }

      if (item.amountExempt !== undefined) {
        newAmountExempt = newAmountExempt
          ? newAmountExempt + item.amountExempt
          : item.amountExempt;
      }

      if (item.taxed10Percent !== undefined) {
        newTaxed10Percent = newTaxed10Percent
          ? newTaxed10Percent + item.taxed10Percent
          : item.taxed10Percent;
      }

      if (item.taxed5Percent !== undefined) {
        newTaxed5Percent = newTaxed5Percent
          ? newTaxed5Percent + item.taxed5Percent
          : item.taxed5Percent;
      }

      if (item.iva10Percent !== undefined) {
        newIva10Percent = newIva10Percent
          ? newIva10Percent + item.iva10Percent
          : item.iva10Percent;
      }

      if (item.iva5Percent !== undefined) {
        newIva5Percent = newIva5Percent
          ? newIva5Percent + item.iva5Percent
          : item.iva5Percent;
      }
    }

    setTotalAmount(newTotalAmount);
    setAmount10Percent(newAmount10Percent);
    setAmount5Percent(newAmount5Percent);
    setAmountExempt(newAmountExempt);
    setIva10Percent(newIva10Percent);
    setIva5Percent(newIva5Percent);

    debitNoteRef.current = {
      ...debitNoteRef.current,
      items: newItems,
      amount: newTotalAmount,
      amount10Percent: newAmount10Percent,
      amount5Percent: newAmount5Percent,
      amountExempt: newAmountExempt,
      taxed10Percent: newTaxed10Percent,
      taxed5Percent: newTaxed5Percent,
      iva10Percent: newIva10Percent,
      iva5Percent: newIva5Percent,
    };

    setItems(newItems);
  };

  const onAddItemClick = () => {
    if (debitNoteRef.current === null) return;
    const newItems = [...debitNoteRef.current.items, newDebitNoteItem()];
    onItemsChange(newItems);
  };

  const value: DebitNoteFormContextValue = {
    debitNoteRef,
    debitNoteDate,
    currency,
    pygExchangeRate,
    totalAmount,
    amount10Percent,
    amount5Percent,
    amountExempt,
    iva10Percent,
    iva5Percent,
    establishment,
    dispatchPoint,
    stamp,
    client,
    associatedDocumentType,
    electronicInvoice,
    items,
    errors,
    readonly,
    setDebitNoteDate,
    setCurrency,
    setPygExchangeRate,
    setEstablishment,
    setDispatchPoint,
    setStamp,
    setClient,
    setAssociatedDocumentType,
    setElectronicInvoice,
    setItems,
    setErrors,
    onEstablishmentChange,
    onDispatchPointChange,
    onStampChange,
    onClientChange,
    onDebitNoteDateChange,
    onCurrencyChange,
    onAssociatedDocumentTypeChange,
    onElectronicInvoiceChange,
    onAssociatedDocumentStampIdentifierChange,
    onAssociatedDocumentEstablishmentCodeChange,
    onAssociatedDocumentDispatchPointCodeChange,
    onAssociatedDocumentNumberChange,
    onAssociatedDocumentDateChange,
    onPygExchangeRateChange,
    onDncpContractCodeChange,
    onDncpContractModalityChange,
    onDncpContractDateChange,
    onObsChange,
    onItemChange,
    onItemDelete,
    onItemsChange,
    onAddItemClick,
  };

  return (
    <DebitNoteFormContext.Provider value={value}>
      {children}
    </DebitNoteFormContext.Provider>
  );
};
